import axios, {
    AxiosError,
    AxiosRequestConfig,
    AxiosResponse,
    CustomParamsSerializer,
    Method,
    ParamsSerializerOptions,
} from 'axios';
import { Session } from '~/models/auth';
import getRuntimeConfig from '~/services/runtime-config.service';
import { cxpSignOut } from '~/utils/auth';
import { getCachedSession } from '~/utils/session-cache';

export interface ApiOptions<P> {
    params?: P;
    paramsSerializer?: ParamsSerializerOptions | CustomParamsSerializer;
    responseType?: AxiosRequestConfig['responseType'];
}

export type ApiResponse<T = unknown> = {
    data?: T;
    statusCode: number;
    message: string;
    errorCodes?: string[];
};

type TAppName =
    | 'CXP'
    | 'CXP_INBOX'
    | 'REMINDERS'
    | 'IAC'
    | 'AVPV'
    | 'FORM_RECOGNIZER'
    | 'ADMIN'
    | 'PRODUCT_RENEWAL'
    | 'TSTX'
    | 'TSTX_ORDER'
    | 'CHC'
    | 'FORM_SERVICE'
    | 'DLA'
    | 'EHOCM'
    | 'VAU'
    | 'T2Signature';

const responseInterceptor = async (response: AxiosResponse<any, any>) => {
    return response;
};

// Any status codes that falls outside the range of 2xx cause this function to trigger
const errorInterceptor = async (error: AxiosError) => {
    if (error?.response?.status === 401) {
        const session = (await getCachedSession()) as Session;
        if (session.idToken) {
            cxpSignOut(session.idToken);
        }
    }
    return Promise.reject(error);
};

axios.interceptors.response.use(responseInterceptor, errorInterceptor);

const getApiBasePath = async (appName: TAppName) => {
    const runtimeConfig = await getRuntimeConfig();
    switch (appName) {
        case 'CXP':
        default:
            return runtimeConfig.cxpApiBasePath;
        case 'REMINDERS':
            return runtimeConfig.remindersApiBasePath;
        case 'FORM_RECOGNIZER':
            return runtimeConfig.formRecognizerBasePath;
        case 'IAC':
            return runtimeConfig.cxpIACApiBasePath;
        case 'ADMIN':
            return runtimeConfig.adminServiceHost;
        case 'PRODUCT_RENEWAL':
            return runtimeConfig.productRenewalApiBasePath;
        case 'TSTX':
            return runtimeConfig.txtxMtoBasePath;
        case 'TSTX_ORDER':
            return runtimeConfig.tstxMtoOrderApiPath;
        case 'CHC':
            return runtimeConfig.chcApiBasePath;
        case 'FORM_SERVICE':
            return runtimeConfig.formServiceBasePath;
        case 'DLA':
            return runtimeConfig.cxpDlaApiBasePath;
        case 'EHOCM':
            return runtimeConfig.ehocmApiBasePath;
        case 'VAU':
            return runtimeConfig.cxpVauApiBasePath;
        case 'T2Signature':
            return runtimeConfig.t2SignatureApiBasePath;
    }
};

const getAuthorizationHeader = async (appName: TAppName) => {
    const session = (
        [
            'CXP',
            'CXP_INBOX',
            'IAC',
            'PRODUCT_RENEWAL',
            'TSTX',
            'TSTX_ORDER',
            'DLA',
            'FORM_SERVICE',
            'EHOCM',
            'VAU',
            'T2Signature',
        ] as TAppName[]
    ).includes(appName)
        ? ((await getCachedSession()) as Session)
        : undefined;

    return session?.accessToken ? 'Bearer ' + session.accessToken : undefined;
};

const buildGetConfig = async <P>(
    appName: TAppName,
    url: string,
    options?: ApiOptions<P>,
    headers?: AxiosRequestConfig['headers']
): Promise<AxiosRequestConfig> => {
    return {
        url,
        params: options?.params,
        paramsSerializer: options?.paramsSerializer,
        responseType: options?.responseType,
        baseURL: `${await getApiBasePath(appName)}/`,
        headers: {
            Authorization: await getAuthorizationHeader(appName),
            'x-session-id': sessionStorage.getItem('x-session-id'),
            ...headers,
        },
    };
};

const mapResponse = <R = unknown>(
    response: AxiosResponse<ApiResponse<R>>,
    appName?: TAppName
): ApiResponse<R> => {
    const result = {
        data:
            appName &&
            ['CHC', 'DLA', 'EHOCM', 'FORM_SERVICE', 'CXP_INBOX', 'VAU', 'T2Signature'].includes(
                appName
            )
                ? (response.data as unknown as R)
                : response?.data?.data,
        statusCode: response?.status,
        message: response?.data?.message ?? response?.statusText,
        errorCodes: response?.data?.errorCodes,
    } as ApiResponse<R>;

    if (Array.isArray(result?.errorCodes) && result?.errorCodes?.length > 0) {
        throw result;
    }
    return result;
};

const buildConfig = async <D = unknown, P = Record<string, unknown>>(
    appName: TAppName,
    url: string,
    method: Method,
    data?: D,
    options?: ApiOptions<P>,
    headers?: AxiosRequestConfig['headers']
): Promise<AxiosRequestConfig<D>> => {
    return {
        url,
        data,
        method,
        headers: {
            Authorization: await getAuthorizationHeader(appName),
            ...headers,
            'Content-Type': 'application/json-patch+json',
            'x-session-id': sessionStorage.getItem('x-session-id'),
        },
        params: options?.params,
        paramsSerializer: options?.paramsSerializer,
        baseURL: await getApiBasePath(appName),
    };
};

const create = async (appName: TAppName, headers?: AxiosRequestConfig['headers']) => {
    return axios
        .create({
            baseURL: await getApiBasePath(appName),
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                authorization: await getAuthorizationHeader(appName),
                'x-session-id': sessionStorage.getItem('x-session-id'),
                ...headers,
            },
        })
        .interceptors.response.use(responseInterceptor, errorInterceptor);
};

const get = async <R, P = Record<string, unknown>>(
    appName: TAppName,
    url: string,
    options?: ApiOptions<P>,
    headers?: AxiosRequestConfig['headers']
): Promise<ApiResponse<R>> => {
    const result = await axios.request<ApiResponse<R>>(
        await buildGetConfig(appName, url, options, headers)
    );

    return mapResponse(result, appName);
};

const post = async <R = Record<string, unknown>, P = Record<string, unknown>>(
    appName: TAppName,
    url: string,
    data: unknown,
    headers?: AxiosRequestConfig['headers'],
    options?: ApiOptions<P>
) => {
    const result = await axios.request<ApiResponse<R>>(
        await buildConfig(appName, url, 'POST', data, options, headers)
    );
    return mapResponse(result, appName);
};

const put = async <R = Record<string, unknown>, D = R>(
    appName: TAppName,
    url: string,
    data: D,
    headers?: AxiosRequestConfig['headers']
) => {
    const result = await axios.request<ApiResponse<R>>(
        await buildConfig(appName, url, 'PUT', data, undefined, headers)
    );
    return mapResponse(result);
};

const postBase = async <R = Record<string, unknown>, P = Record<string, unknown>>(
    url: string,
    data: unknown,
    headers?: AxiosRequestConfig['headers'],
    options?: ApiOptions<P>
) => {
    const result = await axios.post<ApiResponse<R>>(url, data, {
        headers,
        params: options?.params,
    });
    return mapResponse(result);
};

const postForm = async <R = Record<string, unknown>>(
    appName: TAppName,
    url: string,
    body: unknown,
    headers?: AxiosRequestConfig['headers']
) => {
    const baseUrl = await getApiBasePath(appName);
    const result = await axios.post<ApiResponse<R>>(`${baseUrl}${url}`, body, { headers });
    return mapResponse(result);
};

const remove = async <R = Record<string, unknown>, D = R>(
    appName: TAppName,
    url: string,
    data?: D,
    headers?: AxiosRequestConfig['headers']
) => {
    const result = await axios.request<ApiResponse<R>>(
        await buildConfig(appName, url, 'DELETE', data, undefined, headers)
    );
    return mapResponse(result);
};

const patch = async <R = Record<string, unknown>, D = R>(
    appName: TAppName,
    url: string,
    data: D,
    headers?: AxiosRequestConfig['headers']
) => {
    const result = await axios.request<ApiResponse<R>>(
        await buildConfig(appName, url, 'PATCH', data, undefined, headers)
    );
    return mapResponse(result);
};

export const api = {
    create,
    get,
    post,
    put,
    patch,
    delete: remove,
    postBase,
    postForm,
};
